import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import shoobMark from '../../../assets/shoob-mark.png';
import styles from './ConfirmStudent.module.scss';

// "Which child is this?" -- reached from the link we text or email a signup we
// could not match once the school's photos went live.
//
// IT ALREADY KNOWS WHAT THEY TOLD US. Asking a parent to retype their child's
// name does not merely annoy: it throws away the near-miss that made them
// unmatchable. Someone who typed "Katie" against a roster saying "Kathryn" is
// exactly who this page is for, and a blank form loses that.
//
// STUDENT ID IS REQUIRED HERE, though it was optional at signup. At signup no
// roster existed to check one against and requiring it would have blocked the
// case the screen exists for. By the time this page is reached the roster
// exists AND every softer match has already failed, so the id is both askable
// and the thing that settles it.
const STRINGS = {
  en: {
    title: 'Confirm your student',
    school: 'School',
    firstName: 'First name',
    lastName: 'Last name',
    grade: 'Grade',
    studentId: 'Student ID',
    studentIdHelp: 'On your child’s school ID card, report card or school portal.',
    submit: 'Find my photos',
    saving: 'Checking…',
    matched: 'Verified! We’ll send your access code shortly.',
    // Says the ID didn't verify, NOT that the child could not be found. Alex's
    // rule: never suggest anything has been lost. Deliberately not a dead end
    // either -- the details can be right and the roster wrong, and the lookup
    // page has a photo-matching fallback.
    unmatched: 'That ID didn’t verify. Please double-check it, or look your code up here:',
    lookup: 'Look up your code →',
    expired: 'This link has expired. You can look your code up here:',
    required: 'Please enter your student’s ID.',
    failed: 'Something went wrong. Please try again.',
  },
  es: {
    title: 'Confirme a su estudiante',
    school: 'Escuela',
    firstName: 'Nombre',
    lastName: 'Apellido',
    grade: 'Grado',
    studentId: 'Número de estudiante',
    studentIdHelp: 'En la credencial escolar, la boleta de calificaciones o el portal de la escuela.',
    submit: 'Buscar mis fotos',
    saving: 'Verificando…',
    matched: '¡Verificado! Le enviaremos su código de acceso en breve.',
    unmatched: 'Ese número no se pudo verificar. Verifíquelo o busque su código aquí:',
    lookup: 'Buscar su código →',
    expired: 'Este enlace ha vencido. Puede buscar su código aquí:',
    required: 'Ingrese el número de estudiante.',
    failed: 'Algo salió mal. Intente de nuevo.',
  },
};

const ConfirmStudent = () => {
  const { token } = useParams();
  const lang = new URLSearchParams(window.location.search).get('lang') === 'es' ? 'es' : 'en';
  const t = STRINGS[lang] || STRINGS.en;

  const [prefill, setPrefill] = useState(null);
  const [expired, setExpired] = useState(false);
  const [studentId, setStudentId] = useState('');
  const [state, setState] = useState('idle');

  // `baseURL: '/'` on every call: the customer app sets a global axios baseURL
  // of /api/v1 and this route is not under it.
  useEffect(() => {
    axios.get(`/picture_day_signups/${token}`, { baseURL: '/' })
      .then(({ data }) => {
        setPrefill(data);
        setStudentId(data.studentId || '');
      })
      .catch(() => setExpired(true));
  }, [token]);

  const submit = (event) => {
    if (event) event.preventDefault();
    if (!studentId.trim()) { setState('required'); return; }

    setState('saving');
    axios.patch(`/picture_day_signups/${token}`, { student_id: studentId }, { baseURL: '/' })
      .then(({ data }) => setState(data.matched ? 'matched' : 'unmatched'))
      .catch((error) => setState(error?.response?.status === 404 ? 'expired' : 'failed'));
  };

  const lookupLink = (
    <a className={styles.lookup} href={`/c/get-access-code?lang=${lang}`}>{t.lookup}</a>
  );

  if (expired || state === 'expired') {
    return (
      <div className={styles.page} lang={lang}>
        <div className={styles.card}>
          <p className={styles.message}>{t.expired}</p>
          {lookupLink}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page} lang={lang}>
      <div className={styles.card}>
        <div className={styles.logoBadge} aria-hidden="true">
          <img src={shoobMark} alt="" className={styles.logoMark} />
        </div>
        {/* No subtitle. The heading already says what this page is, and a
            line explaining it again only pushed the one field further down. */}
        <h1 className={styles.title}>{t.title}</h1>

        {state === 'matched' && (
          <p className={styles.matched} role="status">{t.matched}</p>
        )}

        {state !== 'matched' && (
          <form onSubmit={submit}>
            {/* What they already told us, shown as read-only context rather than
                fields to re-fill. Editing them cannot help: the id is what
                resolves this, and re-opening the name invites a second wrong
                guess. */}
            {prefill && (
              <dl className={styles.known}>
                <dt>{t.school}</dt>
                <dd>{prefill.schoolName}</dd>
                {prefill.firstName && (
                  <>
                    <dt>{t.firstName}</dt>
                    <dd>{prefill.firstName}</dd>
                  </>
                )}
                {prefill.lastName && (
                  <>
                    <dt>{t.lastName}</dt>
                    <dd>{prefill.lastName}</dd>
                  </>
                )}
                {prefill.grade && (
                  <>
                    <dt>{t.grade}</dt>
                    <dd>{prefill.grade}</dd>
                  </>
                )}
              </dl>
            )}

            <div className={styles.field}>
              <label htmlFor="confirm-student-id">{t.studentId}</label>
              <input
                id="confirm-student-id"
                type="text"
                value={studentId}
                onChange={(event) => setStudentId(event.target.value)}
              />
              <p className={styles.help}>{t.studentIdHelp}</p>
            </div>

            <button type="submit" className={styles.submit} disabled={state === 'saving'}>
              {state === 'saving' ? t.saving : t.submit}
            </button>

            {state === 'required' && <p className={styles.error} role="alert">{t.required}</p>}
            {state === 'failed' && <p className={styles.error} role="alert">{t.failed}</p>}

            {state === 'unmatched' && (
              <div className={styles.error} role="alert">
                <p>{t.unmatched}</p>
                {lookupLink}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default ConfirmStudent;
